import React, { useState, useEffect } from 'react';
import { Minus, Plus, X, CheckCircle2, AlertCircle, Calculator, Sparkles } from 'lucide-react';
import './AdmissionsFormInputs.css';

/**
 * Helper to convert an amount in Rupiah to a concise human-readable Indonesian phrase.
 * Example: 12500000 -> "12,5 Juta Rupiah"
 */
export const formatTerbilangRingkas = (num) => {
  const n = Math.abs(Number(num) || 0);
  if (n === 0) return 'Nol Rupiah';
  if (n >= 1_000_000_000) {
    const b = n / 1_000_000_000;
    const formatted = b % 1 === 0 ? b.toString() : b.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
    return `${formatted} Miliar Rupiah`;
  }
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    const formatted = m % 1 === 0 ? m.toString() : m.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
    return `${formatted} Juta Rupiah`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    const formatted = k % 1 === 0 ? k.toString() : k.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
    return `${formatted} Ribu Rupiah`;
  }
  return `${n} Rupiah`;
};

/**
 * Format a number to ID currency string with dots (e.g. 12.500.000)
 */
export const formatRupiahDisplay = (val) => {
  const num = Number(val) || 0;
  return num.toLocaleString('id-ID');
};

/**
 * CurrencyInput component:
 * - Prefix badge: Rp
 * - Automatic thousand separator dots while typing
 * - Strips leading zeros (e.g. 012500000 -> 12.500.000)
 * - Human readable sub-caption (e.g. 12,5 Juta Rupiah)
 * - Clear button [X]
 */
export const CurrencyInput = ({
  value = 0,
  onChange,
  placeholder = '0',
  disabled = false,
  readOnly = false,
  required = false,
  showTerbilang = true,
  helperText,
  className = '',
  id,
  name,
  min = 0,
  max,
  size = 'md',
}) => {
  const numericVal = Number(value) || 0;
  const [displayStr, setDisplayStr] = useState(numericVal > 0 ? numericVal.toLocaleString('id-ID') : '0');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // Keep displayStr synced with incoming prop if not currently editing actively or if value changed externally
    const num = Number(value) || 0;
    setDisplayStr(num > 0 ? num.toLocaleString('id-ID') : '0');
  }, [value]);

  const handleInputChange = (e) => {
    // Strip everything except digits
    const rawDigits = e.target.value.replace(/\D/g, '');
    
    // Parse to clean integer (automatically removes leading zeros like 012500000 -> 12500000)
    let parsed = rawDigits ? parseInt(rawDigits, 10) : 0;
    
    if (min !== undefined && parsed < min) parsed = min;
    if (max !== undefined && parsed > max) parsed = max;

    setDisplayStr(parsed > 0 ? parsed.toLocaleString('id-ID') : '0');

    if (typeof onChange === 'function') {
      onChange(parsed);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // If current value is 0, select all so typing immediately replaces it cleanly
  };

  const handleBlur = () => {
    setIsFocused(false);
    const num = Number(value) || 0;
    setDisplayStr(num > 0 ? num.toLocaleString('id-ID') : '0');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setDisplayStr('0');
    if (typeof onChange === 'function') {
      onChange(0);
    }
  };

  return (
    <div className={`adm-currency-wrapper ${size} ${isFocused ? 'is-focused' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}>
      <div className="adm-currency-input-box">
        <span className="adm-currency-prefix">Rp</span>
        <input
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          value={displayStr}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className="adm-currency-native-input"
          onSelect={(e) => {
            // If value is 0 on focus, auto-select it for easy overwriting
            if (displayStr === '0' && isFocused) {
              e.target.select();
            }
          }}
        />
        {!disabled && !readOnly && numericVal > 0 && (
          <button
            type="button"
            className="adm-currency-clear-btn"
            onClick={handleClear}
            title="Reset ke Rp 0"
            tabIndex={-1}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {showTerbilang && (
        <div className="adm-currency-helper-row">
          <span className="adm-currency-terbilang">
            Rp {numericVal.toLocaleString('id-ID')} &bull; <em>{formatTerbilangRingkas(numericVal)}</em>
          </span>
          {helperText && <span className="adm-currency-subhelp">{helperText}</span>}
        </div>
      )}
    </div>
  );
};

/**
 * QuantityStepperInput component:
 * - Decrement [-] button
 * - Clean direct numeric input with inputMode="numeric"
 * - Increment [+] button
 * - Unit badge (e.g. Kursi, Peserta, Voucher)
 * - Quick addition chips (+5, +10)
 */
export const QuantityStepperInput = ({
  value = 0,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  unit = 'Kursi',
  quickAdds = [5, 10],
  disabled = false,
  badge,
  badgeBg = '#eff6ff',
  badgeColor = '#1d4ed8',
  className = '',
}) => {
  const numericVal = Math.max(min, Number(value) || 0);

  const handleStep = (delta) => {
    if (disabled) return;
    const nextVal = Math.min(max, Math.max(min, numericVal + delta));
    if (typeof onChange === 'function') {
      onChange(nextVal);
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    let parsed = raw ? parseInt(raw, 10) : min;
    if (parsed > max) parsed = max;
    if (parsed < min) parsed = min;
    if (typeof onChange === 'function') {
      onChange(parsed);
    }
  };

  return (
    <div className={`adm-stepper-container ${disabled ? 'is-disabled' : ''} ${className}`}>
      {badge && (
        <div className="adm-stepper-header-badge" style={{ background: badgeBg, color: badgeColor }}>
          {badge}
        </div>
      )}
      <div className="adm-stepper-controls">
        <button
          type="button"
          className="adm-stepper-btn btn-dec"
          onClick={() => handleStep(-step)}
          disabled={disabled || numericVal <= min}
          title={`Kurangi ${step}`}
          tabIndex={-1}
        >
          <Minus size={14} />
        </button>

        <div className="adm-stepper-input-wrap">
          <input
            type="text"
            inputMode="numeric"
            value={numericVal}
            onChange={handleInputChange}
            disabled={disabled}
            className="adm-stepper-input"
          />
          {unit && <span className="adm-stepper-unit">{unit}</span>}
        </div>

        <button
          type="button"
          className="adm-stepper-btn btn-inc"
          onClick={() => handleStep(step)}
          disabled={disabled || numericVal >= max}
          title={`Tambah ${step}`}
          tabIndex={-1}
        >
          <Plus size={14} />
        </button>
      </div>

      {quickAdds && quickAdds.length > 0 && !disabled && (
        <div className="adm-stepper-quick-chips">
          {quickAdds.map((addVal) => (
            <button
              key={addVal}
              type="button"
              className="adm-quick-add-chip"
              onClick={() => handleStep(addVal)}
              tabIndex={-1}
            >
              +{addVal}
            </button>
          ))}
          <button
            type="button"
            className="adm-quick-add-chip chip-reset"
            onClick={() => onChange && onChange(min)}
            tabIndex={-1}
            title="Reset ke nilai minimum"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * DevelopmentFeeTerminCalculator component:
 * Real-time reconciliation of Development Fee vs Termin 1, 2, 3
 * Gives live visual feedback (Balanced / Under / Over) and 1-click smart split presets
 */
export const DevelopmentFeeTerminCalculator = ({
  developmentFee = 0,
  df1 = 0,
  df2 = 0,
  df3 = 0,
  onApplySplit,
}) => {
  const totalDF = Number(developmentFee) || 0;
  const val1 = Number(df1) || 0;
  const val2 = Number(df2) || 0;
  const val3 = Number(df3) || 0;
  const sumTermin = val1 + val2 + val3;
  const diff = totalDF - sumTermin;

  const isBalanced = totalDF > 0 && diff === 0;
  const isUnder = totalDF > 0 && diff > 0;
  const isOver = totalDF > 0 && diff < 0;

  // Split calculation helpers
  const applyPreset = (presetType) => {
    if (typeof onApplySplit !== 'function') return;
    if (totalDF <= 0) return;

    if (presetType === '50_25_25') {
      const part1 = Math.round(totalDF * 0.5);
      const part2 = Math.round(totalDF * 0.25);
      const part3 = totalDF - part1 - part2;
      onApplySplit({ df1: part1, df2: part2, df3: part3 });
    } else if (presetType === 'full_t1') {
      onApplySplit({ df1: totalDF, df2: 0, df3: 0 });
    } else if (presetType === 'equal_3') {
      const part1 = Math.floor(totalDF / 3);
      const part2 = Math.floor(totalDF / 3);
      const part3 = totalDF - part1 - part2;
      onApplySplit({ df1: part1, df2: part2, df3: part3 });
    } else if (presetType === '40_30_30') {
      const part1 = Math.round(totalDF * 0.4);
      const part2 = Math.round(totalDF * 0.3);
      const part3 = totalDF - part1 - part2;
      onApplySplit({ df1: part1, df2: part2, df3: part3 });
    }
  };

  return (
    <div className={`adm-df-calc-card ${isBalanced ? 'status-balanced' : isUnder ? 'status-under' : isOver ? 'status-over' : ''}`}>
      <div className="adm-df-calc-header">
        <div className="adm-df-calc-title">
          <Calculator size={16} />
          <span>Kalkulator &amp; Rekonsiliasi Termin Uang Pangkal (Development Fee)</span>
        </div>
        <div className="adm-df-calc-status-pill">
          {totalDF === 0 && <span className="pill-neutral">Total Uang Pangkal Rp 0</span>}
          {isBalanced && (
            <span className="pill-success">
              <CheckCircle2 size={13} /> Sesuai 100% (Rp {sumTermin.toLocaleString('id-ID')})
            </span>
          )}
          {isUnder && (
            <span className="pill-warning">
              <AlertCircle size={13} /> Kurang Rp {diff.toLocaleString('id-ID')}
            </span>
          )}
          {isOver && (
            <span className="pill-danger">
              <AlertCircle size={13} /> Berlebih Rp {Math.abs(diff).toLocaleString('id-ID')}
            </span>
          )}
        </div>
      </div>

      <div className="adm-df-calc-summary-grid">
        <div className="adm-df-calc-item">
          <span className="item-label">Total Uang Pangkal (DF)</span>
          <strong className="item-value">Rp {totalDF.toLocaleString('id-ID')}</strong>
        </div>
        <div className="adm-df-calc-item">
          <span className="item-label">Termin DF 1</span>
          <span className="item-subval">Rp {val1.toLocaleString('id-ID')}</span>
        </div>
        <div className="adm-df-calc-item">
          <span className="item-label">Termin DF 2</span>
          <span className="item-subval">Rp {val2.toLocaleString('id-ID')}</span>
        </div>
        <div className="adm-df-calc-item">
          <span className="item-label">Termin DF 3</span>
          <span className="item-subval">Rp {val3.toLocaleString('id-ID')}</span>
        </div>
        <div className="adm-df-calc-item highlight">
          <span className="item-label">Total 3 Termin</span>
          <strong className="item-value">Rp {sumTermin.toLocaleString('id-ID')}</strong>
        </div>
      </div>

      {totalDF > 0 && (
        <div className="adm-df-calc-presets">
          <span className="presets-label">
            <Sparkles size={13} /> Opsi Cepat Bagi Termin:
          </span>
          <div className="presets-actions">
            <button
              type="button"
              className="adm-preset-btn"
              onClick={() => applyPreset('50_25_25')}
              title="50% saat daftar ulang, 25% termin 2, 25% termin 3"
            >
              50% - 25% - 25%
            </button>
            <button
              type="button"
              className="adm-preset-btn"
              onClick={() => applyPreset('40_30_30')}
              title="40% saat daftar ulang, 30% termin 2, 30% termin 3"
            >
              40% - 30% - 30%
            </button>
            <button
              type="button"
              className="adm-preset-btn"
              onClick={() => applyPreset('equal_3')}
              title="Bagi rata ke dalam 3 kali termin cicilan"
            >
              Bagi Rata (3x)
            </button>
            <button
              type="button"
              className="adm-preset-btn"
              onClick={() => applyPreset('full_t1')}
              title="100% lunas langsung di Termin 1 (Daftar Uang)"
            >
              100% di Termin 1
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
